import React, { memo, useCallback, useEffect, useState } from "react";
import baseUrl from "../../../api/baseUrl";
import AddPurpose from "./AddPurpose";
import { useDispatch } from "react-redux";
import { setPurpose } from "../../../redux/appointment/appointmentAction";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import FlagIcon from "@mui/icons-material/Flag";
import SearchIcon from "@mui/icons-material/Search";

interface Purpose {
  _id: string;
  purposeName: string;
  color?: string;
}

interface TableRowProps {
  item: Purpose;
  index: number;
  onUpdate: (id: string) => void;
}

const ColorSwatch: React.FC<{ color?: string }> = ({ color }) => {
  if (!color) {
    return <span className="text-xs text-slate-400 dark:text-slate-500">—</span>;
  }
  return (
    <span className="inline-flex items-center gap-2">
      <span
        className="w-3.5 h-3.5 rounded-full border border-slate-200 dark:border-slate-600 shrink-0"
        style={{ backgroundColor: color }}
      />
      <span
        className="px-2.5 py-0.5 rounded-full text-xs font-medium text-slate-700 dark:text-slate-200 capitalize border border-slate-200/60 dark:border-slate-600"
        style={{ backgroundColor: color }}
      >
        {color}
      </span>
    </span>
  );
};

const TableRow: React.FC<TableRowProps> = memo(({ item, index, onUpdate }) => (
  <tr className="hover:bg-slate-50/50 dark:hover:bg-slate-700/50 transition-colors">
    <td className="px-6 py-3">
      <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400">
        {index + 1}
      </span>
    </td>
    <td className="px-6 py-3">
      <div className="flex items-center gap-3">
        <div
          className="w-9 h-9 rounded-full flex items-center justify-center text-slate-700"
          style={
            item.color
              ? { backgroundColor: item.color }
              : {
                  background: "linear-gradient(to bottom right, #fbbf24, #f97316)",
                  color: "#fff",
                }
          }
        >
          <FlagIcon sx={{ fontSize: 18 }} />
        </div>
        <span className="font-medium text-slate-800 dark:text-slate-200">{item.purposeName}</span>
      </div>
    </td>
    <td className="px-6 py-3">
      <ColorSwatch color={item.color} />
    </td>
    <td className="px-6 py-3">
      <button
        onClick={() => onUpdate(item._id)}
        className="p-2 rounded-lg bg-indigo-50 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-800/50 transition-colors"
        title="Edit"
      >
        <EditIcon sx={{ fontSize: 18 }} />
      </button>
    </td>
  </tr>
));

const MobileCard: React.FC<TableRowProps> = memo(({ item, index, onUpdate }) => (
  <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-4 border border-slate-100 dark:border-slate-700">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center text-slate-700"
          style={
            item.color
              ? { backgroundColor: item.color }
              : {
                  background: "linear-gradient(to bottom right, #fbbf24, #f97316)",
                  color: "#fff",
                }
          }
        >
          <FlagIcon sx={{ fontSize: 20 }} />
        </div>
        <div>
          <p className="font-semibold text-slate-800 dark:text-slate-200">{item.purposeName}</p>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-xs text-slate-400 dark:text-slate-500">#{index + 1}</span>
            {item.color && <ColorSwatch color={item.color} />}
          </div>
        </div>
      </div>
      <button
        onClick={() => onUpdate(item._id)}
        className="p-2 bg-indigo-50 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-800/50 transition-colors"
      >
        <EditIcon sx={{ fontSize: 18 }} />
      </button>
    </div>
  </div>
));

const ManagePurpose: React.FC = () => {
  const [open, setOpen] = useState<boolean>(false);
  const [data, setData] = useState<Purpose[]>([]);
  const [id, setId] = useState<string>("");
  const [search, setSearch] = useState("");
  const dispatch = useDispatch();

  const getData = useCallback(() => {
    baseUrl
      .get("client/setting/purpose/findAllPurpose")
      .then((response) => {
        setData(response.data.data);
        dispatch(setPurpose(response.data.data, false));
      })
      .catch((error) => console.error(error?.response));
  }, [dispatch]);

  useEffect(() => {
    getData();
  }, [getData]);

  const closeModal = useCallback(() => {
    setOpen(false);
    setId("");
    getData();
  }, [getData]);

  const openModal = useCallback(() => {
    setId("");
    setOpen(true);
  }, []);
  
  const update = useCallback((id: string) => {
    setId(id);
    setOpen(true);
  }, []);

  const filteredData = data.filter((item) =>
    item.purposeName.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <>

      {/* Header Card */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg shadow-slate-200/50 dark:shadow-slate-900/50 border border-slate-100 dark:border-slate-700 p-4 mt-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <SearchIcon
              className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500"
              sx={{ fontSize: 20 }}
            />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search purpose..."
              className="w-full pl-12 pr-4 py-2.5 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all text-slate-800 dark:text-slate-200 placeholder-slate-500 dark:placeholder-slate-400"
            />
          </div>

          <div className="flex items-center gap-4">
            {/* Count Badge */}
            <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 dark:bg-amber-900/40 rounded-xl">
              <FlagIcon className="text-amber-500 dark:text-amber-400" sx={{ fontSize: 20 }} />
              <span className="font-bold text-amber-600 dark:text-amber-400">{data.length}</span>
              <span className="text-slate-500 dark:text-slate-400 text-sm">Purposes</span>
            </div>

            {/* Add Button */}
            <button
              onClick={() => openModal()}
              className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-primary to-primaryLight text-white rounded-xl hover:shadow-lg hover:shadow-primary/20 transition-all font-medium"
            >
              <AddIcon sx={{ fontSize: 20 }} />
              Add Purpose
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg shadow-slate-200/50 dark:shadow-slate-900/50 border border-slate-100 dark:border-slate-700 overflow-hidden mt-4">
        {/* Mobile Cards */}
        <div className="md:hidden p-4 space-y-3">
          {filteredData.length === 0 ? (
            <div className="text-center py-12 text-slate-400 dark:text-slate-500">
              <FlagIcon sx={{ fontSize: 48 }} className="mb-2" />
              <p>No purposes found</p>
            </div>
          ) : (
            filteredData.map((item, i) => (
              <MobileCard key={item._id} item={item} index={i} onUpdate={update} />
            ))
          )}
        </div>

        {/* Desktop Table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50/80 dark:bg-slate-700/50">
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  No
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  Purpose Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  Color
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
              {filteredData.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-12 text-center text-slate-400 dark:text-slate-500">
                    <FlagIcon sx={{ fontSize: 48 }} className="mb-2" />
                    <p>No purposes found</p>
                  </td>
                </tr>
              ) : (
                filteredData.map((item, i) => (
                  <TableRow key={item._id} item={item} index={i} onUpdate={update} />
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {open && <AddPurpose id={id} closeModal={closeModal} />}
    </>
  );
};

export default ManagePurpose;
